import { flattenProp, branch, renderNothing } from 'recompose'
import { isDesktop } from 'shared/responsive-helpers'

import { purchaseCompletedSelector, locationIdSelector, numberOfItemsSelector } from 'basket/selectors/'
import { userSelector } from 'auth/selectors'

import { withRouteParams, loadOnMount } from 'shared/hoc'
import scrollTop from 'shared/scroll-top'
import Pin from '@components/Pin'
import { MarkerStyle } from 'shared/generate-marker-style'
import Button from '@components/Button'

import { withRouter, withTranslatedMeta } from 'i18n/utils'
import { SEARCH_MODE_HOURLY, SEARCH_MODE_MONTHLY, SEARCH_MODE_EVENTS, SEARCH_MODE_PREPAID } from 'search/constants'
import { selectedEventSelector } from 'search/selectors'
import featureToggles from 'shared/featureToggles'

import { getBookingLocationDetails, setParkingMode } from 'booking/actions'
import { locationDetailsPendingSelector, locationDetailsSelector, stateSelector, rateSelector } from 'booking/selectors'

import { CardSection } from '@components/CardSection'
import { Map } from 'booking/components/Map'
import { ParkingInfoSideBar } from 'booking/components/ParkingInfoSideBar'
import MonthlySelection from 'booking/components/MonthlySelection'
import EventsSelection from 'booking/components/EventsSelection'
import PrepaidSelection from '@booking/components/PrepaidSelection'
import HourlySelection from '@booking/components/HourlySelection'
import BookingViewHeader from '@booking/components/BookingView/BookingViewHeader'

import ConfirmSection from '../ConfirmSection'

import 'booking/styles.scss'

const BookingView = ({
    location,
    defaultCustomerFlow,
    parkingFlowUrlParameter,
    detailsPending,
    purchaseCompleted,
    name,
    grsId,
    geoLocation,
    history,
    basketLocationId,
    rate,
    selectedSeasonTicket,
    setParkingMode,
    parkingMode,
    numberOfItemsInBasket,
    selectedEvent,
    user,
}) => {
    const getRate = () => {
        switch (parkingMode) {
            case SEARCH_MODE_HOURLY:
                return rate
            case SEARCH_MODE_MONTHLY:
                return selectedSeasonTicket.rate
            case SEARCH_MODE_EVENTS:
                if (featureToggles.hasEventFlow) {
                    return selectedEvent
                }
                break
            default:
                return rate
        }
    }

    const getTicketSelection = () => {
        switch (parkingMode) {
            case SEARCH_MODE_HOURLY:
                return <HourlySelection grsId={grsId} />
            case SEARCH_MODE_PREPAID:
                return <PrepaidSelection grsId={grsId} />
            case SEARCH_MODE_MONTHLY:
                return <MonthlySelection grsId={grsId} />
            case SEARCH_MODE_EVENTS:
                if (featureToggles.hasEventFlow) {
                    return <EventsSelection grsId={grsId} />
                }
                break
            default:
                return <HourlySelection grsId={grsId} />
        }
    }

    const setBookingFlow = () => {
        if (!parkingFlowUrlParameter && parkingMode !== SEARCH_MODE_HOURLY && parkingMode !== SEARCH_MODE_PREPAID) {
            setParkingMode(SEARCH_MODE_HOURLY)
        } else if (parkingFlowUrlParameter && parkingMode !== parkingFlowUrlParameter) {
            if (!featureToggles.hasEventFlow && parkingFlowUrlParameter === SEARCH_MODE_EVENTS) {
                return
            }
            setParkingMode(parkingFlowUrlParameter)
        }
    }

    React.useEffect(() => {
        setBookingFlow()
    }, [parkingFlowUrlParameter])

    React.useEffect(() => {
        scrollTop()
    }, [numberOfItemsInBasket])

    React.useEffect(() => {
        purchaseCompleted && history.push('confirmation')
    }, [purchaseCompleted])

    const ticketSelection = React.useMemo(() => getTicketSelection(), [grsId, parkingMode])

    return (
        <div className="booking">
            <main className="booking__main">
                <BookingViewHeader
                    defaultCustomerFlow={defaultCustomerFlow}
                    grsId={grsId}
                    name={name}
                    parkingMode={parkingMode}
                />
                {basketLocationId && basketLocationId !== grsId ? (
                    <CardSection
                        className="restriction-message"
                        title={<T>booking.restrict-multiple-locations.title</T>}>
                        <p>
                            <T>booking.restrict-multiple-locations.text</T>
                        </p>
                        <div className="row">
                            <div className="col-xs-12 col-sm-4">
                                <Button block href="/map" btnStyle="outline">
                                    <T>booking.restrict-multiple-locations.button</T>
                                </Button>
                            </div>
                        </div>
                    </CardSection>
                ) : (
                    ticketSelection
                )}
                {(parkingMode !== SEARCH_MODE_PREPAID || user) && <ConfirmSection grsId={grsId} rate={getRate()} />}
            </main>
            {isDesktop() && (
                <ParkingInfoSideBar loading={detailsPending} location={location}>
                    {geoLocation && (
                        <Map geoLocation={geoLocation}>
                            <div style={MarkerStyle(30, 30)}>
                                <Pin name={name} />
                            </div>
                        </Map>
                    )}
                </ParkingInfoSideBar>
            )}
        </div>
    )
}

export default compose(
    withTranslatedMeta('booking.title'),
    withRouter,
    withRouteParams,
    connect(
        createStructuredSelector({
            numberOfItemsInBasket: numberOfItemsSelector,
            purchaseCompleted: purchaseCompletedSelector,
            detailsPending: locationDetailsPendingSelector,
            location: locationDetailsSelector,
            basketLocationId: locationIdSelector,
            rate: rateSelector,
            bookingState: stateSelector,
            selectedEvent: selectedEventSelector,
            user: userSelector,
        }),
        {
            getBookingLocationDetails,
            setParkingMode,
        }
    ),
    loadOnMount('getBookingLocationDetails', ({ grsId }) => [grsId]),
    branch(props => !props.location, renderNothing),
    flattenProp('location'),
    flattenProp('address'),
    flattenProp('bookingState')
)(BookingView)
